(() => {
  'use strict';

  const MOBILE_QUERY = '(max-width: 768px)';
  const galleries = new WeakMap();
  let updateTimer = null;

  const normalise = (value) => String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

  const productRoot = (gallery) =>
    gallery.closest('[data-section-type="product"]') ||
    gallery.closest('.product-section') ||
    gallery.closest('section') ||
    document;

  const selectedColour = (root) => {
    const checked = Array.from(root.querySelectorAll('input[type="radio"]:checked'))
      .find((input) => /^(color|colour|kleur)$/i.test(input.name || ''));

    if (checked && checked.value) return checked.value;

    const select = Array.from(root.querySelectorAll('select'))
      .find((element) => /^(color|colour|kleur)$/i.test(element.name || ''));

    if (select && select.value) return select.value;

    const label = root.querySelector('[data-variant-color-label]');
    return label ? label.textContent.trim() : '';
  };

  const flickityInstance = (gallery) => {
    if (window.Flickity && typeof window.Flickity.data === 'function') {
      const instance = window.Flickity.data(gallery);
      if (instance) return instance;
    }

    return gallery.flickity || gallery._flickity || null;
  };

  const rememberOriginalIndex = (element) => {
    if (!element || Object.prototype.hasOwnProperty.call(element.dataset, 'originalIndex')) {
      return;
    }

    element.dataset.originalIndex = element.dataset.index || '';
  };

  const slideKey = (slide) =>
    `${slide.dataset.group || ''}:${slide.dataset.originalIndex || slide.dataset.index || ''}`;

  const prepareVisibleSlides = (slides) => {
    slides.forEach((slide, index) => {
      rememberOriginalIndex(slide);

      slide.dataset.index = String(index);
      slide.dataset.filteredIndex = String(index);
      slide.classList.toggle('starting-slide', index === 0);
      slide.classList.toggle('secondary-slide', index !== 0);
      slide.classList.remove('is-selected');

      slide.style.top = '0px';
      slide.style.marginTop = '0px';
      slide.style.left = '';
      slide.style.removeProperty('transform');
    });
  };

  const selectedCellElement = (instance) => {
    if (instance.selectedElement) {
      return instance.selectedElement;
    }

    const selectedIndex = Number.isInteger(instance.selectedIndex)
      ? instance.selectedIndex
      : 0;

    if (instance.cells && instance.cells[selectedIndex]) {
      return instance.cells[selectedIndex].element;
    }

    if (instance.cells && instance.cells.length) {
      return instance.cells[0].element;
    }

    return null;
  };

  const syncGalleryHeight = (instance) => {
    if (!instance || !instance.viewport || !instance.slider) return;

    const cell = selectedCellElement(instance);
    if (!cell) return;

    const imageWrap = cell.querySelector('.image-wrap:not(.hide)');
    const measuredElement = imageWrap || cell.querySelector('.product-image-main') || cell;
    const rect = measuredElement.getBoundingClientRect();
    const height = Math.ceil(rect.height || measuredElement.scrollHeight || 0);

    if (!height) return;

    instance.cells.forEach((flickityCell) => {
      if (!flickityCell.element) return;
      flickityCell.element.style.top = '0px';
      flickityCell.element.style.marginTop = '0px';
    });

    instance.viewport.style.height = `${height}px`;
    instance.viewport.style.minHeight = '0px';
    instance.viewport.scrollTop = 0;

    instance.slider.style.height = `${height}px`;
    instance.slider.style.top = '0px';
    instance.slider.style.marginTop = '0px';
  };

  const stabiliseFlickity = (instance, forceFirst = false) => {
    if (!instance || !instance.cells || !instance.cells.length) return;

    if (forceFirst && typeof instance.select === 'function') {
      instance.select(0, false, true);
    }

    if (typeof instance.reposition === 'function') {
      instance.reposition();
    }

    if (typeof instance.positionSliderAtSelected === 'function') {
      instance.positionSliderAtSelected();
    }

    if (typeof instance.setGallerySize === 'function') {
      instance.setGallerySize();
    }

    if (typeof instance.resize === 'function') {
      instance.resize();
    }

    if (forceFirst && typeof instance.select === 'function') {
      instance.select(0, false, true);
    }

    syncGalleryHeight(instance);
  };

  const clearSettleTimers = (state) => {
    state.settleTimers.forEach((timer) => window.clearTimeout(timer));
    state.settleTimers = [];
  };

  const settleFlickity = (state, instance) => {
    clearSettleTimers(state);

    // A new colour should begin on its first image once.
    stabiliseFlickity(instance, true);

    // Later layout repairs preserve whichever thumbnail the customer selected.
    const repair = () => stabiliseFlickity(instance, false);
    state.settleTimers = [40, 100, 220, 450, 900, 1500].map((delay) =>
      window.setTimeout(repair, delay)
    );

    const firstCell = instance.cells && instance.cells[0]
      ? instance.cells[0].element
      : null;

    if (!firstCell) return;

    firstCell.querySelectorAll('img').forEach((image) => {
      if (!image.complete) {
        image.addEventListener('load', repair, { once: true });
      }
    });
  };

  const bindFlickityEvents = (state, instance) => {
    if (state.boundFlickity === instance) return;

    state.boundFlickity = instance;

    if (typeof instance.on !== 'function') return;

    const updateHeight = () => {
      window.requestAnimationFrame(() => syncGalleryHeight(instance));
    };

    instance.on('select', updateHeight);
    instance.on('settle', updateHeight);
  };

  const replaceFlickitySlides = (state, instance, matchingSlides) => {
    prepareVisibleSlides(matchingSlides);

    const currentSlides = typeof instance.getCellElements === 'function'
      ? instance.getCellElements()
      : Array.from(instance.slider.children);

    if (
      typeof instance.remove === 'function' &&
      typeof instance.append === 'function'
    ) {
      if (currentSlides.length) {
        instance.remove(currentSlides);
      }

      instance.append(matchingSlides);
    } else {
      state.slides.forEach((slide) => {
        if (slide.parentNode) slide.parentNode.removeChild(slide);
      });

      matchingSlides.forEach((slide) => instance.slider.appendChild(slide));

      if (typeof instance.reloadCells === 'function') {
        instance.reloadCells();
      }
    }

    if (typeof instance.reloadCells === 'function') {
      instance.reloadCells();
    }

    if (instance.pageDots && typeof instance.pageDots.setDots === 'function') {
      instance.pageDots.setDots();
    }

    if (typeof instance.updateDraggable === 'function') {
      instance.updateDraggable();
    }

    bindFlickityEvents(state, instance);
    settleFlickity(state, instance);
  };

  const filterThumbnails = (root, group, resetActive = false) => {
    root.querySelectorAll('[data-product-thumbs]').forEach((container) => {
      const items = Array.from(container.querySelectorAll(
        '.product__thumb-item[data-set-name="color"][data-group]'
      ));

      if (!items.length) return;

      let visibleIndex = 0;
      let firstVisible = null;

      items.forEach((item) => {
        const show = item.dataset.group === group;
        const link = item.querySelector('[data-product-thumb]');

        rememberOriginalIndex(item);
        rememberOriginalIndex(link);

        item.hidden = !show;
        item.setAttribute('aria-hidden', show ? 'false' : 'true');
        item.classList.toggle('hide', !show);

        if (show) {
          item.style.removeProperty('display');
          item.dataset.index = String(visibleIndex);

          if (link) {
            link.dataset.index = String(visibleIndex);
            link.removeAttribute('tabindex');
          }

          if (!firstVisible) firstVisible = item;
          visibleIndex += 1;
        } else {
          item.style.setProperty('display', 'none', 'important');

          if (link) {
            link.setAttribute('tabindex', '-1');
          }
        }
      });

      if (!resetActive) return;

      items.forEach((item) => {
        const link = item.querySelector('[data-product-thumb]');
        item.classList.remove('is-active', 'current', 'is-selected');
        if (link) link.classList.remove('is-active', 'current', 'is-selected');
      });

      if (firstVisible) {
        const firstLink = firstVisible.querySelector('[data-product-thumb]');
        firstVisible.classList.add('is-active');
        if (firstLink) firstLink.classList.add('is-active');
      }

      const scroller = container.querySelector('.product__thumbs--scroller');
      if (scroller) scroller.scrollLeft = 0;
      container.scrollLeft = 0;
    });
  };

  const initialiseGallery = (gallery) => {
    if (galleries.has(gallery)) return galleries.get(gallery);

    if (!window.matchMedia(MOBILE_QUERY).matches) return null;

    const slides = Array.from(
      gallery.querySelectorAll('.product-main-slide[data-set-name="color"][data-group]')
    );

    if (!slides.length) return null;

    slides.forEach(rememberOriginalIndex);

    const state = {
      root: productRoot(gallery),
      slides,
      activeGroup: '',
      settleTimers: [],
      boundFlickity: null
    };

    galleries.set(gallery, state);
    return state;
  };

  const filterGallery = (gallery) => {
    const state = initialiseGallery(gallery);
    if (!state) return;

    const colour = selectedColour(state.root);
    const colourSlug = normalise(colour);
    if (!colourSlug) return;

    const group = `color_${colourSlug}`;
    const matchingSlides = state.slides.filter((slide) => slide.dataset.group === group);
    if (!matchingSlides.length) return;

    const groupChanged = state.activeGroup !== group;
    filterThumbnails(state.root, group, groupChanged);

    const flickity = flickityInstance(gallery);
    if (!flickity || !flickity.slider) return;

    bindFlickityEvents(state, flickity);

    const currentKeys = Array.from(flickity.slider.children)
      .map(slideKey)
      .join('|');
    const desiredKeys = matchingSlides
      .map(slideKey)
      .join('|');

    const needsRebuild =
      groupChanged ||
      currentKeys !== desiredKeys ||
      flickity.cells.length !== matchingSlides.length;

    if (!needsRebuild) {
      // The colour group is already correct. Do not select index 0 again:
      // this allows thumbnails 2, 3 and 4 and swiping to remain selected.
      return;
    }

    replaceFlickitySlides(state, flickity, matchingSlides);
    state.activeGroup = group;
  };

  const filterAll = () => {
    if (!window.matchMedia(MOBILE_QUERY).matches) return;

    document
      .querySelectorAll('[data-product-photos].product-slideshow')
      .forEach(filterGallery);
  };

  const scheduleFilter = (delay = 80) => {
    window.clearTimeout(updateTimer);
    updateTimer = window.setTimeout(filterAll, delay);
  };

  const scheduleBurst = () => {
    [0, 50, 120, 260, 500, 900, 1500].forEach((delay) => {
      window.setTimeout(filterAll, delay);
    });
  };

  const cancelGalleryRepairs = (target) => {
    const thumb = target.closest('[data-product-thumb]');
    if (!thumb) return;

    const root =
      thumb.closest('[data-section-type="product"]') ||
      thumb.closest('.product-section') ||
      thumb.closest('section');

    if (!root) return;

    root.querySelectorAll('[data-product-photos].product-slideshow').forEach((gallery) => {
      const state = galleries.get(gallery);
      if (state) clearSettleTimers(state);
    });
  };

  document.addEventListener('pointerdown', (event) => {
    cancelGalleryRepairs(event.target);
  }, true);

  document.addEventListener('change', scheduleBurst, true);
  document.addEventListener('click', (event) => {
    if (event.target.closest('.variant-input-wrap, [data-variant-input], .swatch-view-item')) {
      scheduleBurst();
    }
  }, true);

  document.addEventListener('variant:change', scheduleBurst);
  document.addEventListener('variantChange', scheduleBurst);
  document.addEventListener('shopify:section:load', () => scheduleFilter(150));
  window.addEventListener('resize', () => scheduleFilter(80));

  const start = () => {
    scheduleBurst();

    // Keep supporting older swatch apps, but an unchanged colour group is now a no-op.
    window.setInterval(filterAll, 600);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();
